import React from 'react';
import { ui } from '../../config/uiSizes';
import {
    isSnapshotIncompleteWarning,
    stripSnapshotIncompletePrefix,
} from '../../utils/migrationWarnings';

interface SnapshotIncompleteBannerProps {
    /** Raw export warnings (e.g. `job1Export.warnings`). Non-snapshot-completeness lines are ignored. */
    warnings?: string[] | undefined;
    /** Optional extra classes for the outer container. */
    className?: string;
}

/**
 * Amber banner shown on a snapshot's detail view when one or more draft-only flows could not be
 * captured. Genesys Cloud's CX-as-Code exporter only exports flows that have a published version, so
 * draft-only flows are silently omitted by the platform. This banner frames that honestly: a snapshot
 * backs up your published, deployable configuration, and here is exactly what was left out and why.
 */
const SnapshotIncompleteBanner: React.FC<SnapshotIncompleteBannerProps> = ({
    warnings,
    className = '',
}) => {
    const lines = (warnings ?? [])
        .filter(isSnapshotIncompleteWarning)
        .map(stripSnapshotIncompletePrefix);

    if (lines.length === 0) {
        return null;
    }

    return (
        <div
            className={`bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg ${ui.page.sectionPadding} ${ui.mb.title} ${className}`}
            role="alert"
        >
            <div className={`flex items-center ${ui.gap.sm} ${ui.mb.sm}`}>
                <svg
                    className={`${ui.svg.sm} text-amber-600 dark:text-amber-300 shrink-0`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
                <p className={`${ui.text.label} font-semibold text-amber-900 dark:text-amber-100`}>
                    Some flows were not captured
                </p>
            </div>
            <p className={`${ui.text.xs} text-amber-800 dark:text-amber-200 ${ui.mb.sm}`}>
                A snapshot backs up your published, deployable configuration. Genesys Cloud only exports
                flows that have a published version, so draft-only flows can&rsquo;t be captured. Publish
                these flows and re-run the snapshot to include them.
            </p>
            <ul className={`flex flex-col ${ui.gap.xs}`}>
                {lines.map((line, index) => (
                    <li
                        key={index}
                        className={`${ui.text.xs} text-amber-700 dark:text-amber-300 break-words`}
                    >
                        {line}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default SnapshotIncompleteBanner;
